import { APP_INITIALIZER, DEFAULT_CURRENCY_CODE, LOCALE_ID, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { RouterModule } from '@angular/router';
import { AppComponent } from './app.component';
import { appRoutes } from './app.routes';
import { LoadAppConfigService, LocaleImportsService, setGlobalConfig } from '@fh-client-interaction-frontends/fh-providers';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { HttpClient, HttpClientModule, provideHttpClient, withInterceptors } from '@angular/common/http';
import { forkJoin } from 'rxjs';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { FhLoadingModule } from '@fh-client-interaction-frontends/shared/ui/fh-loading';
import { FhToasterMessagesModule } from '@fh-client-interaction-frontends/shared/ui/fh-toaster-messages';
import { APP_CONFIG, EnvironmentModel } from '@fh-client-interaction-frontends/fh-app-config';
import { FhSideBarModule } from '@fh-client-interaction-frontends/fh-side-bar';
import { cpErrorsInterceptor, cpHeadersInterceptor } from '@fh-client-interaction-frontends/fh-client-portal-service';
import { fhCamelCasePaginationInterceptor } from '../../../../libs/shared/data-access/fh-interceptors-service/src/lib/helpers/interceptors/fh-camel-case-pagination/fh-camel-case-pagination.interceptor';
import { fhNullRequestsInterceptor } from '../../../../libs/shared/data-access/fh-interceptors-service/src/lib/helpers/interceptors/fh-null-requests/fh-null-requests.interceptor';
import { FhMagnifierModule } from '@fh-client-interaction-frontends/fh-magnifier';
import { GoogleTagManagerModule } from 'angular-google-tag-manager';

async function initializeApp(
  http: HttpClient,
  translateService: TranslateService,
  localeService: LocaleImportsService,
  configService: LoadAppConfigService
): Promise<void> {
  // Carregar configuração do JSON
  const response = await fetch('assets/config/config-partnership.json');
  if (!response.ok) throw new Error('Falha ao carregar configuração');

  // Juntar setup estático com o setup de ambiente (QA, TRAIN e PROD)
  const jsonConfig = await response.json();

  // Carregar configuração de estática JSON
  const partnership = await fetch('assets/environments/partnership.json');
  if (!partnership.ok) throw new Error('Falha ao carregar configuração');

  // Juntar setup estático com o setup de ambiente (QA, TRAIN e PROD)
  const jsonPartnershipConfig = await partnership.json();
  let config = {...jsonPartnershipConfig, ...jsonConfig };

  configService.config = config;
  setGlobalConfig(configService.config);

  // Configurar traduções
  const mergedConfig = configService.config;
  translateService.addLangs([mergedConfig.i18n.defaultLang]);
  const lang = translateService.getLangs().includes(navigator.language) ? navigator.language : mergedConfig.i18n.defaultLang;

  // @ts-ignore
  const [base, partnershipTranslations] = await forkJoin([
    http.get<any>(`assets/i18n/${lang}.json`),
    http.get<any>(`assets/i18n/${lang}.partnership.json`)
  ]).toPromise();

  translateService.setTranslation(lang, base);
  translateService.setTranslation(lang, partnershipTranslations, true);
  await translateService.use(lang).toPromise();

  // Configurar localidade
  await localeService.loadLocale(mergedConfig.locale);
}

export function HttpLoaderFactory(http: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(http);
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    RouterModule.forRoot(appRoutes, {
      initialNavigation: 'enabledBlocking'
    }),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      }
    }),
    FhLoadingModule,
    FhToasterMessagesModule,
    FhSideBarModule,
    FhMagnifierModule,
    GoogleTagManagerModule
  ],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: (http: HttpClient, translate: TranslateService, locale: LocaleImportsService, config: LoadAppConfigService) => () =>
        initializeApp(http, translate, locale, config),
      deps: [HttpClient, TranslateService, LocaleImportsService, LoadAppConfigService],
      multi: true
    },
    {
      provide: APP_CONFIG,
      useFactory: (configService: LoadAppConfigService) => configService.config,
      deps: [LoadAppConfigService]
    },
    {
      provide: LOCALE_ID,
      useFactory: (config: EnvironmentModel) => config.locale,
      deps: [APP_CONFIG]
    },
    {
      provide: DEFAULT_CURRENCY_CODE,
      useFactory: (config: EnvironmentModel) => config.currencyCode,
      deps: [APP_CONFIG]
    },
    {
      provide: 'googleTagManagerId',
      useFactory: (appConfig: EnvironmentModel) => appConfig.scripts?.googleAnalytics?.data?.trackingId || '',
      deps: [APP_CONFIG]
    },
    provideHttpClient(withInterceptors([cpErrorsInterceptor, cpHeadersInterceptor, fhCamelCasePaginationInterceptor, fhNullRequestsInterceptor]))
  ],
  bootstrap: [AppComponent]
})
export class AppModule {}
